export interface TitleExtractionResult {
  title: string;
  // True only for the "# TITLE" heading pattern (FIRST PASS) — the one signal reliable
  // enough that a later match should be allowed to overwrite an already-set title.
  isHeaderMatch: boolean;
}

// Returns the extracted title plus which pass matched it, so callers can tell a
// high-confidence "# TITLE" heading apart from a low-confidence bold-text guess.
export const extractTitleWithConfidence = (messages: any[]): TitleExtractionResult | null => {
  if (!messages || messages.length === 0) return null;

  const filteredMessages = messages.filter(message => {
    const content = (message.content || "").toLowerCase();
    return !(
      content.includes("initial questions") ||
      content.includes("clarification") ||
      content.includes("# questions") ||
      content.includes("## questions")
    );
  });

  // Primary pattern: # TITLE (most reliable - this is how Claude formats titles).
  // Capture the whole H1 line rather than whitelisting characters — titles routinely
  // contain commas, ampersands, question marks etc. ("Blood, Blackmail & Bollinger"),
  // which a restrictive character class silently rejected, dropping the title entirely.
  // `## Premise` etc. don't match because `\s+` requires a space right after the single `#`.
  const headerTitlePattern = /^#\s+(.+?)\s*$/m;

  // Secondary patterns (fallbacks)
  const titleLabelPattern = /title:\s*["']?([^"'\n]+)["']?/i;
  const boldTitlePattern = /\*\*([A-Z][A-Za-z0-9\s:'\-']+?)\*\*/;

  // FIRST PASS: Look for # headers only (most reliable indicator of actual title)
  for (const message of filteredMessages) {
    if (message.role === 'assistant' || message.is_ai) {
      const content = message.content || '';

      const headerMatch = content.match(headerTitlePattern);
      if (headerMatch && headerMatch[1] && headerMatch[1].trim()) {
        // Strip markdown bold and any surrounding quotes the capture may include.
        const title = stripMetaSuffix(
          headerMatch[1]
            .replace(/\*\*/g, '')
            .replace(/^["'""'']+|["'""'']+$/g, '')
            .trim()
        );
        // Validate it looks like a title (not a section header like "Questions" or "Character 1")
        if (title && /[A-Za-z]/.test(title) && !isLikelySectionHeader(title)) {
          return { title: formatTitle(title), isHeaderMatch: true };
        }
      }
    }
  }

  // SECOND PASS: Look for "Title:" label pattern
  for (const message of filteredMessages) {
    if (message.role === 'assistant' || message.is_ai) {
      const content = message.content || '';

      const titleMatch = content.match(titleLabelPattern);
      if (titleMatch && titleMatch[1] && titleMatch[1].trim()) {
        const title = stripMetaSuffix(titleMatch[1].trim());
        if (!isLikelySectionHeader(title)) {
          return { title: formatTitle(title), isHeaderMatch: false };
        }
      }
    }
  }

  // THIRD PASS: Look for bold text that looks like a title (usually early in message)
  for (const message of filteredMessages) {
    if (message.role === 'assistant' || message.is_ai) {
      const content = message.content || '';

      // Only check first 500 chars to avoid grabbing random bold text
      const firstPart = content.substring(0, 500);
      const boldMatch = firstPart.match(boldTitlePattern);
      if (boldMatch && boldMatch[1] && boldMatch[1].trim()) {
        const title = stripMetaSuffix(boldMatch[1].trim());
        if (!isLikelySectionHeader(title) && looksLikeTitle(title)) {
          return { title: formatTitle(title), isHeaderMatch: false };
        }
      }
    }
  }

  return null;
};

// Backward-compatible string-only wrapper for callers that don't need match confidence.
export const extractTitleFromMessages = (messages: any[]): string | null => {
  return extractTitleWithConfidence(messages)?.title ?? null;
};

// Strips a dash-separated meta descriptor the AI sometimes appends to its own
// recap/summary headings (e.g. "# The Night The Storm Hit — Full Final Recap").
// Requires a dash separator so legitimate colon-separated subtitles
// ("Dead On Arrival: The Blackwood Victory") are never affected.
const metaSuffixPattern = /\s*[—–-]\s*(full\s+)?(final\s+)?(recap|summary|overview|draft)\s*$/i;
const stripMetaSuffix = (title: string): string => title.replace(metaSuffixPattern, '').trim();

// Check if text is likely a section header rather than a mystery title
const isLikelySectionHeader = (text: string): boolean => {
  const sectionHeaders = [
    'questions', 'character', 'background', 'round', 'evidence',
    'clues', 'motives', 'relationships', 'introduction', 'overview',
    'instructions', 'setup', 'premise', 'victim', 'suspect', 'detective',
    'for example', 'example'
  ];
  const lower = text.toLowerCase();
  return sectionHeaders.some(header => lower.startsWith(header) || lower === header);
};

// Check if text looks like a mystery title
const looksLikeTitle = (text: string): boolean => {
  // Titles usually have multiple words and contain murder/mystery/death keywords
  // Or are formatted like "The Something Something"
  const words = text.split(/\s+/);
  if (words.length < 2 || words.length > 10) return false;

  const lower = text.toLowerCase();

  // Reject obvious non-titles: bold labels/instructions that happen to contain "mystery"
  // (e.g. "**AFTER you generate the mystery package:**" leaked in as a title before this guard).
  if (text.trim().endsWith(':')) return false;
  const instructionPrefixes = [
    'after ', 'before ', 'when ', 'while ', 'once ', 'how ', 'why ',
    'here ', 'now ', 'next ', 'first ', 'finally ',
    'i can ', 'i will ', 'i\'ll ', 'i\'ve ', 'you can ', 'you will ',
    'let me ', 'let\'s ', 'note: ', 'tip: ',
  ];
  if (instructionPrefixes.some(p => lower.startsWith(p))) return false;

  const titleKeywords = ['murder', 'mystery', 'death', 'deadly', 'blood', 'killer', 'crime'];
  const startsWithThe = lower.startsWith('the ') || lower.startsWith('a ');

  return titleKeywords.some(kw => lower.includes(kw)) || startsWithThe;
};

export const formatTitle = (title: string) => {
  // First strip markdown bold markers
  const cleanTitle = title.replace(/\*\*/g, '');
  return cleanTitle
    .trim()
    .split(' ')
    .map(word => {
      // Handle words with apostrophes - don't capitalize after apostrophes
      if (word.includes("'") || word.includes("'")) {
        const parts = word.split(/([''])/);
        return parts.map((part, index) => {
          if (index === 0) {
            return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
          } else if (part === "'" || part === "'") {
            return part;
          } else {
            return part.toLowerCase();
          }
        }).join('');
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};
